// @ts-nocheck -- verbatim TAPP payroll UI compatibility boundary
import { Modal as HeroModal } from "@heroui/react";
import { IconX } from "@tabler/icons-react";
import { clsx } from "clsx";
import { useEffect, useId, useRef, type ReactNode } from "react";

export type ModalSize = "sm" | "md" | "lg" | "xl" | "full";
export type ModalScroll = "inside" | "outside";

export interface ModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  size?: ModalSize;
  scroll?: ModalScroll;
  isDismissable?: boolean;
  isKeyboardDismissDisabled?: boolean;
  closeLabel?: string;
  className?: string;
}

const heroSize: Record<ModalSize, "sm" | "md" | "lg" | "full"> = {
  sm: "sm",
  md: "md",
  lg: "lg",
  xl: "lg",
  full: "full",
};

export function Modal({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  size = "md",
  scroll = "outside",
  isDismissable = true,
  isKeyboardDismissDisabled = false,
  closeLabel = "Close dialog",
  className,
}: ModalProps) {
  const restoreFocusRef = useRef<HTMLElement | null>(null);
  const restoreFocusFrameRef = useRef<number | null>(null);
  const restoreFocusTimeoutRef = useRef<number | null>(null);
  const wasOpenRef = useRef(false);
  const descriptionId = useId();

  const cancelPendingFocusRestoration = () => {
    const frame = restoreFocusFrameRef.current;
    const timeout = restoreFocusTimeoutRef.current;
    restoreFocusFrameRef.current = null;
    restoreFocusTimeoutRef.current = null;
    if (typeof window === "undefined") return;
    if (frame !== null) window.cancelAnimationFrame(frame);
    if (timeout !== null) window.clearTimeout(timeout);
  };

  if (open && !wasOpenRef.current) {
    restoreFocusRef.current = typeof document !== "undefined" && document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
  }
  wasOpenRef.current = open;

  useEffect(() => {
    if (!open) return;
    cancelPendingFocusRestoration();
    return () => {
      cancelPendingFocusRestoration();
      const element = restoreFocusRef.current;
      const restoreFocus = () => {
        if (typeof document === "undefined") return;
        const activeElement = document.activeElement;
        if (element?.isConnected && (!activeElement || activeElement === document.body || !activeElement.isConnected)) element.focus();
      };
      if (typeof window === "undefined") return;
      restoreFocusFrameRef.current = window.requestAnimationFrame(() => {
        restoreFocusFrameRef.current = null;
        restoreFocus();
        if (typeof window === "undefined") return;
        restoreFocusTimeoutRef.current = window.setTimeout(() => {
          restoreFocusTimeoutRef.current = null;
          restoreFocus();
        }, 250);
      });
    };
  }, [open]);

  useEffect(() => () => cancelPendingFocusRestoration(), []);

  return (
    <HeroModal.Root isOpen={open} onOpenChange={onOpenChange}>
      <HeroModal.Backdrop
        isDismissable={isDismissable}
        isKeyboardDismissDisabled={isKeyboardDismissDisabled}
        variant="opaque"
        className="tapp-modal__backdrop"
      >
        <HeroModal.Container
          placement="center"
          scroll={scroll}
          size={heroSize[size]}
          className="tapp-modal__container"
        >
          <HeroModal.Dialog
            aria-describedby={description ? descriptionId : undefined}
            className={clsx(
              "tapp-modal__dialog",
              size === "xl" && "tapp-modal__dialog--xl",
              scroll === "inside" && "tapp-modal__dialog--scroll-inside",
              className,
            )}
          >
            <HeroModal.Header className="tapp-modal__header">
              <div className="min-w-0">
                <HeroModal.Heading className="tapp-modal__heading">
                  {title}
                </HeroModal.Heading>
                {description && (
                  <p id={descriptionId} className="tapp-modal__description">
                    {description}
                  </p>
                )}
              </div>
              <HeroModal.CloseTrigger
                aria-label={closeLabel}
                className="tapp-modal__close"
              >
                <IconX size={18} aria-hidden="true" />
              </HeroModal.CloseTrigger>
            </HeroModal.Header>
            <HeroModal.Body className="tapp-modal__body">
              {children}
            </HeroModal.Body>
            {footer && (
              <HeroModal.Footer className="tapp-modal__footer">
                {footer}
              </HeroModal.Footer>
            )}
          </HeroModal.Dialog>
        </HeroModal.Container>
      </HeroModal.Backdrop>
    </HeroModal.Root>
  );
}
